<template>
  <GuidesLayout>
    <div class="wrap">
      <div v-if="loading" class="guides-loading">Loading guide…</div>
      <div v-else-if="!post" class="guide-article">
        <div class="guides-empty">
          <b>{{ notFound ? 'Guide not found' : 'Something went wrong' }}</b>
          {{ notFound ? 'This guide may have moved or been taken down.' : error }}
          <router-link to="/guides">Browse all guides</router-link>.
        </div>
      </div>
      <article v-else class="guide-article">
        <nav class="guide-crumb" aria-label="Breadcrumb">
          <router-link to="/guides">Guides</router-link><span>/</span>
          <router-link :to="categoryPath(post.categorySlug)">{{ post.category }}</router-link>
        </nav>
        <div class="guide-hero" :class="`crop-${(post.mobileCropRatio || '1:1').replace(':', '-')}`">
          <img v-if="post.heroImageUrl" :src="post.heroImageUrl" :alt="post.heroImageAlt || ''">
        </div>
        <router-link class="guide-eyebrow" :to="categoryPath(post.categorySlug)">{{ post.category }}</router-link>
        <h1 class="guide-title">{{ post.title }}</h1>
        <div class="guide-meta">
          <span>{{ formatGuideDate(post.publishedAt) }}</span><span class="sep">·</span><span>{{ post.readMinutes }} min read</span>
          <button type="button" class="card-share" @click="copyLink(guideUrl(post.slug))">Copy link</button>
        </div>
        <p v-if="post.metaDescription" class="guide-lede">{{ post.metaDescription }}</p>

        <GuideCta />
        <!-- Body HTML is sanitised server-side (BlogService.sanitizeBody) -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="guide-body" v-html="post.body" />
        <GuideCta />

        <div v-if="post.tags?.length" class="guide-tags" aria-label="Tags">
          <router-link v-for="t in post.tags" :key="t" :to="tagPath(t)">{{ t }}</router-link>
        </div>
      </article>
    </div>
  </GuidesLayout>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import apiService from '../services/api'
import GuidesLayout from '../components/guides/GuidesLayout.vue'
import GuideCta from '../components/guides/GuideCta.vue'
import { categoryPath, formatGuideDate, guidePath, guideUrl, tagPath } from '../components/guides/guideFormat'
import { useGuideToast } from '../components/guides/useGuideToast'
import { absoluteUrl, clearPageMeta, setPageMeta } from '../utils/seo'

const props = defineProps({ slug: { type: String, required: true } })

const { copyLink } = useGuideToast()
const route = useRoute()
const post = ref(null)
const loading = ref(true)
const notFound = ref(false)
const error = ref('')

const updateMeta = () => {
  if (!post.value) {
    setPageMeta({ title: 'Guide not found — Forever-X', path: guidePath(props.slug), robots: 'noindex, follow' })
    return
  }
  const p = post.value
  setPageMeta({
    title: `${p.title} — Forever-X Guides`,
    description: p.metaDescription,
    path: guidePath(p.slug),
    image: p.heroImageUrl,
    type: 'article',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: p.title,
      description: p.metaDescription,
      image: p.heroImageUrl ? [p.heroImageUrl] : undefined,
      datePublished: p.publishedAt ? new Date(p.publishedAt).toISOString() : undefined,
      dateModified: p.lastUpdated ? new Date(p.lastUpdated).toISOString() : undefined,
      articleSection: p.category,
      keywords: (p.tags || []).join(', ') || undefined,
      mainEntityOfPage: absoluteUrl(guidePath(p.slug)),
      publisher: { '@type': 'Organization', name: 'Forever-X', url: absoluteUrl('/') }
    }
  })
}

const load = async () => {
  loading.value = true
  notFound.value = false
  error.value = ''
  try {
    const result = await apiService.getGuide(props.slug)
    if (result.success) {
      post.value = result.blog
    } else {
      post.value = null
      notFound.value = true
    }
  } catch (e) {
    post.value = null
    notFound.value = e.status === 404
    error.value = e.message || 'Unable to load this guide'
  } finally {
    loading.value = false
    updateMeta()
    window.scrollTo(0, 0)
  }
}

watch(() => props.slug, () => { if (route.name === 'Guide') load() }, { immediate: true })
onBeforeUnmount(() => clearPageMeta())
</script>
